import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Icon,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type IconName,
} from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { preflightReportQueryOptions } from "@/lib/queries";
import type { OpenPreflightReportOptions } from "../../lib/report-open";
import { PreFlightReportPage } from "../../pages/PreFlightReportPage";

const NEXT_REPORT_KEY = "j";
const PREVIOUS_REPORT_KEY = "k";

export interface ReportPeekSheetProps {
  /** Report shown in the peek; `null` closes it. */
  reportId: string | null;
  /** Openable report ids in table order, for previous / next. */
  reportIds: readonly string[];
  /** More reports exist beyond `reportIds`, so its length is not the total. */
  hasMoreReports?: boolean;
  onOpenReport: (reportId: string, options?: OpenPreflightReportOptions) => void;
  onClose: () => void;
}

/**
 * Side peek over the Pre Flight hub. It renders the full report view in a right drawer, so a
 * user can scan reports without losing the hub's scope, filters or scroll. J / K step through
 * the table's current order, and the header can promote the peek to the full page or a new tab.
 */
export function ReportPeekSheet({
  reportId,
  reportIds,
  hasMoreReports = false,
  onOpenReport,
  onClose,
}: ReportPeekSheetProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  // Keep rendering the last report while the sheet animates closed.
  const [shownReportId, setShownReportId] = useState(reportId);
  if (reportId != null && reportId !== shownReportId) {
    setShownReportId(reportId);
  }

  const reportQuery = useQuery(
    preflightReportQueryOptions(activeTenantId, shownReportId ?? ""),
  );
  const position = shownReportId ? reportIds.indexOf(shownReportId) : -1;
  const previousReportId = position > 0 ? reportIds[position - 1] : undefined;
  const nextReportId =
    position >= 0 && position < reportIds.length - 1
      ? reportIds[position + 1]
      : undefined;

  function peek(targetId: string | undefined) {
    if (targetId) onOpenReport(targetId, { mode: "peek" });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!isStepShortcut(event)) return;
    if (event.key === NEXT_REPORT_KEY) peek(nextReportId);
    else peek(previousReportId);
  }

  return (
    <Sheet open={reportId != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        showClose={false}
        className="gap-0"
        onKeyDown={handleKeyDown}
        // Focus the panel, not its first button: a focused toolbar button pops its tooltip,
        // which then swallows the first Escape. J / K also work from the first keypress.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.focus();
          }
        }}
      >
        <SheetTitle className="sr-only">
          {reportQuery.data?.name ??
            t("preflight.peek.titleFallback", "Report preview")}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {t(
            "preflight.peek.description",
            "Quick view of the report. Press J or K to move between reports.",
          )}
        </SheetDescription>

        <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
          <div className="flex items-center gap-1">
            <PeekToolbarButton
              label={t("preflight.peek.previous", "Previous report (K)")}
              icon="chevron-up"
              disabled={!previousReportId}
              onClick={() => peek(previousReportId)}
            />
            <PeekToolbarButton
              label={t("preflight.peek.next", "Next report (J)")}
              icon="chevron-down"
              disabled={!nextReportId}
              onClick={() => peek(nextReportId)}
            />
            {position >= 0 ? (
              <span className="text-muted-foreground ml-1 text-xs tabular-nums">
                {hasMoreReports
                  ? t("preflight.peek.positionOfMore", "{{current}} of {{total}}+", {
                      current: position + 1,
                      total: reportIds.length,
                    })
                  : t("preflight.peek.position", "{{current}} of {{total}}", {
                      current: position + 1,
                      total: reportIds.length,
                    })}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-1">
            <PeekToolbarButton
              label={t("preflight.peek.openFullPage", "Open full page")}
              icon="maximize-2"
              disabled={!shownReportId}
              onClick={() =>
                shownReportId && onOpenReport(shownReportId, { mode: "page" })
              }
            />
            <PeekToolbarButton
              label={t("preflight.peek.openNewTab", "Open in new tab")}
              icon="external-link"
              disabled={!shownReportId}
              onClick={() =>
                shownReportId &&
                onOpenReport(shownReportId, { mode: "new-tab" })
              }
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <SheetClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("preflight.peek.close", "Close preview")}
                  >
                    <Icon name="x" size="sm" color="inherit" />
                  </Button>
                </SheetClose>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("preflight.peek.close", "Close preview")}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Keyed so stepping to another report starts at the top with fresh view state. */}
        <SheetBody key={shownReportId}>
          {shownReportId ? (
            <PreFlightReportPage
              reportId={shownReportId}
              onBack={onClose}
              isPeek
            />
          ) : null}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * J / K step between reports. Typing in a field, a modified keypress, or a keypress inside a
 * nested dialog or menu (portalled outside the sheet, but React still bubbles it here) is not
 * a step.
 */
function isStepShortcut(event: KeyboardEvent<HTMLDivElement>): boolean {
  if (event.key !== NEXT_REPORT_KEY && event.key !== PREVIOUS_REPORT_KEY) {
    return false;
  }
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return false;
  }
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  if (!event.currentTarget.contains(target)) return false;
  return !target.closest("input, textarea, select, [contenteditable='true']");
}

interface PeekToolbarButtonProps {
  label: string;
  icon: IconName;
  disabled?: boolean;
  onClick: () => void;
}

function PeekToolbarButton({
  label,
  icon,
  disabled,
  onClick,
}: PeekToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
        >
          <Icon name={icon} size="sm" color="inherit" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}
